import  { Fragment, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Breadcrumbs, Btn } from "../../../AbstractElements";
import {
  Col,
  Container,
  Row,
  Spinner,
  Alert,
  Button
} from "reactstrap";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { CardSummaryOrder } from "../../Components/order/CardSummaryOrder";
import { CardProductOrder } from "../../Components/order/CardProductOrder";
import { CardShippingOrder } from "../../Components/order/CardShippingOrder";
import { CardUserOrder } from "../../Components/order/CardUserOrder";
import { CardSiigoInvoice } from "../../Components/order/CardSiigoInvoice";
import { ReshipOrderModal } from "../../Components/order/ReshipOrderModal";
import { InterrapidisimoOfficeModal } from "../../Components/order/InterrapidisimoOfficeModal";
import { useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";



export const DetailOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getData, postData } = useDataFetch();
  const queryClient = useQueryClient();
  const [reshipOpen, setReshipOpen] = useState(false);

  const { data, isLoading } = getData(["order", id], `/order/${id}`, {
    enabled: !!id,
  });

  const order = data?.data?.order;

  // Misma regla que el listado de pendientes (findPaidOrders) y create_shipments
  const canShip =
    order?.status === "PAID" &&
    order?.is_paid &&
    ["servientrega", "interrapidisimo"].includes(String(order?.carrier_id || "").toLowerCase());

  // Motivo de Inter cuando exige "reclamo en oficina"; abre el selector de centros.
  const [officeReason, setOfficeReason] = useState(null);

  const { mutate: shipOrder, isPending: isShipping } = postData("/order/ship", {}, {
    onSuccess: (response) => {
      const failure = response?.data?.errors?.[0];
      if (failure?.code === "INTERRAPIDISIMO_OFFICE_REQUIRED") {
        setOfficeReason(failure.error);
        return;
      }
      setOfficeReason(null);
      const error = failure?.error;
      const success = response?.data?.successful?.[0]?.despacho?.message;
      Swal.fire(error ? "Error" : "Orden despachada", error || success || response?.message, error ? "error" : "success");
      queryClient.invalidateQueries({ queryKey: ["order", id] });
      queryClient.invalidateQueries({ queryKey: ["pending-shipment-orders"] });
    },
    onError: (error) => Swal.fire("Error", error.message, "error"),
  });

  const handleShip = async () => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: "¿Despachar esta orden?",
      text: `Se generará la guía con ${order.carrier_id}.`,
      showCancelButton: true,
      confirmButtonText: "Sí, despachar",
      cancelButtonText: "Cancelar",
    });
    if (isConfirmed) shipOrder({ orderIds: [order.id || order._id] });
  };

  const orderId = order?.id || order?._id;
  const isInter = String(order?.carrier_id || "").toLowerCase() === "interrapidisimo";

  const { mutate: checkInterStatus, isPending: isCheckingStatus } = postData(
    `/order/${orderId}/interrapidisimo/status`,
    {},
    {
      onSuccess: (response) => {
        const result = response?.data || {};
        const inter = result.inter_status;
        Swal.fire({
          icon: result.updated ? "success" : "info",
          title: result.updated ? "Estado actualizado" : "Sin cambios",
          html:
            `<p>${result.message || ""}</p>` +
            (inter
              ? `<p><strong>Inter:</strong> ${inter.name} (${inter.code})<br/><small>${new Date(inter.date).toLocaleString()}</small></p>`
              : ""),
        });
        queryClient.invalidateQueries({ queryKey: ["order", id] });
      },
      onError: (error) => Swal.fire("Error", error.message, "error"),
    }
  );

  const handleOrderUpdate = (updatedOrder) => {
    // Invalidar la caché para refrescar los datos
    queryClient.invalidateQueries({ queryKey: ["order", id] });
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" /> Cargando orden...
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-5">
        <h5>No se encontró la orden</h5>
        <Btn attrBtn={{ color: "primary", onClick: () => navigate(-1) }}>
          <i className="fa fa-arrow-left me-1" />
          Volver
        </Btn>
      </div>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs parent="Tienda" title="Detalle Orden" />
      <Container fluid>
        {order.is_returned && (
          <Alert color="warning" className="d-flex justify-content-between align-items-center">
            La transportadora reportó la devolución de esta orden.
            <Button color="primary" size="sm" onClick={() => setReshipOpen(true)}>
              Generar nueva guía
            </Button>
          </Alert>
        )}
        {officeReason && (
          <InterrapidisimoOfficeModal
            orders={[{ orderId: order.id || order._id, reason: officeReason }]}
            toggle={() => setOfficeReason(null)}
            isShipping={isShipping}
            onConfirm={(offices) => shipOrder({ orderIds: Object.keys(offices), offices })}
          />
        )}
        {reshipOpen && (
          <ReshipOrderModal
            order={order}
            isOpen={reshipOpen}
            toggle={() => setReshipOpen(false)}
            onDone={handleOrderUpdate}
          />
        )}
        <Row className="gy-4">
          <Col lg="8">
            <CardSummaryOrder
              order={order}
              actions={
                <>
                  {isInter && order.interrapidisimo_guide && (
                    <Button color="info" size="sm" onClick={() => checkInterStatus({})} disabled={isCheckingStatus}>
                      {isCheckingStatus ? <Spinner size="sm" className="me-1" /> : <i className="fa fa-refresh me-1" />}
                      Validar estado Inter
                    </Button>
                  )}
                  {canShip && (
                    <Button color="primary" size="sm" onClick={handleShip} disabled={isShipping}>
                      {isShipping ? <Spinner size="sm" className="me-1" /> : <i className="fa fa-truck me-1" />}
                      Despachar orden
                    </Button>
                  )}
                </>
              }
            />
            <CardProductOrder order={order}/>
          </Col>

          <Col lg="4">
            <CardShippingOrder order={order} onUpdated={handleOrderUpdate} />
            <CardSiigoInvoice order={order} onOrderUpdate={handleOrderUpdate} />
            <CardUserOrder order={order} />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};